<template>
<div>
  <v-dialog
    v-if="dialog"
    v-model="dialog"
    width="700"
    persistent
  >
    <v-card>
      <v-card-title>
        <v-row class="d-flex  align-center pb-0" no-gutters>
          <v-col class="h6 grey--text text--darken-1" cols="10">
            {{ $t('batchSchedule.label.title') }}
          </v-col>
          <v-col class="text-right pa-0">
            <v-btn @click="confirmClose()" icon>
              <v-icon small>
                mdi-close
              </v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </v-card-title>

      <v-divider />

      <v-tabs v-if="isAllowedCustomer" fixed-tabs v-model="currentTab" color="realEstate">
        <v-tab :disabled="!!notOnUploadStep">
          {{ $t('batchSchedule.label.appointments') }}
        </v-tab>
        <v-tab :disabled="!!notOnUploadStep">
          {{ $t('batchSchedule.label.drafts') }}
        </v-tab>
      </v-tabs>

      <v-card-text class="body-1 pt-5 pr-6 pl-6 pb-5">
          <div v-if="notOnUploadStep">
            <div v-if="!scheduling">
              <v-row>
                <v-col>
                  <span>
                    {{ $t('batchSchedule.message.appointmentsFound') }}
                  </span>
                </v-col>
              </v-row>

              <v-row>
                <v-col>
                  <v-data-table
                    :headers="appointmentsToScheduleHeaders"
                    :items="appointmentsToSchedule"
                    filterable="false"
                    disable-sort
                    :no-results-text="$t('table.noResults')"
                    :footer-props="{
                      'items-per-page-text': '',
                      'items-per-page-options': []
                    }"
                    mobile-breakpoint="20"
                  >
                    <template v-slot:[`item.tipo`]="{ item }">
                      <span class="font-weight-bold">
                        {{
                          item.tipo
                          ? item.tipo
                          : item.type ? item.type : '---' }}
                      </span>
                    </template>

                    <template v-slot:[`item.data`]="{ item }">
                      <span>
                        {{
                          item.data && item.horario
                          ? `${item.data} ${item.horario}`
                          : item.date && item.hour ? `${item.date} ${item.hour}` : '---'
                        }}
                      </span>
                    </template>

                    <template v-slot:[`item.codigo_do_imovel`]="{ item }">
                      <v-chip small >
                        <span>
                          {{
                            item.codigo_do_imovel
                            ? item.codigo_do_imovel
                            : item.realty_code ? item.realty_code : '---'
                          }}
                        </span>
                      </v-chip>
                    </template>
                  </v-data-table>
                </v-col>
              </v-row>

              <v-row no-gutters>
                <v-col>
                  <div class="d-flex align-center">
                    <b class="mr-1">{{ $t('batchSchedule.label.attachment') }}</b>
                    <InformationTooltip :message="$t('batchSchedule.message.attachmentInfo')" />
                  </div>
                  <v-chip class="my-2" v-if="attachment">
                    <v-icon small class="mr-1">
                      mdi-paperclip
                    </v-icon>
                    {{ attachment.name }}
                  </v-chip>
                  <HUpload
                    class="mt-2"
                    @change="handleGetAttachment"
                    :loading="fileLoading"
                    multiple
                    small
                  />
                </v-col>
              </v-row>

              <v-row>
                <v-col class="d-flex justify-start">
                  <v-btn color="primary" outlined @click="resetFields">
                    <v-icon>
                      mdi-chevron-left
                    </v-icon>
                    {{ $t('batchSchedule.buttons.back') }}
                  </v-btn>
                </v-col>
                <v-col class="d-flex justify-end">
                  <v-btn color="primary" elevation="0" @click="schedule">
                    {{ $t('batchSchedule.buttons.schedule') }}
                    <v-icon>
                      mdi-chevron-right
                    </v-icon>
                  </v-btn>
                </v-col>
              </v-row>
            </div>
            <div v-else>
              <v-row>
                <v-col class="d-flex justify-center">
                  <h2>
                    {{ `${currentScheduling}/${appointmentsToSchedule.length}` }}
                  </h2>
                </v-col>
              </v-row>

              <v-row>
                <v-col>
                  <v-progress-linear v-if="!batchImportEnded" indeterminate ></v-progress-linear>
                </v-col>
              </v-row>
              <v-row v-if="batchImportEnded && batchAppointmentsWithError.length > 0">
                <v-col>
                  <v-alert type="error">
                    <span>{{ $t('batchSchedule.message.appointmentsNotImported') }}</span>
                  </v-alert>
                </v-col>
              </v-row>
              <v-row v-if="batchImportEnded && batchAppointmentsWithError.length > 0">
                <v-col>
                  <v-btn color="primary" elevation="0" @click="downloadErrorsFile">
                    {{ $t('batchSchedule.buttons.downloadFile') }}
                  </v-btn>
                </v-col>
              </v-row>
              <v-row>
                <v-col>
                  <v-data-table
                    :headers="headers"
                    :items="batchAppointmentsCombined"
                    filterable="false"
                    disable-sort
                    :no-results-text="$t('table.noResults')"
                    :footer-props="{
                      'items-per-page-text': '',
                      'items-per-page-options': [],
                    }"
                    single-expand
                    :expanded.sync="expanded"
                    show-expand
                    item-key="index"
                    mobile-breakpoint="20"
                  >
                    <template v-slot:[`item.appointment.code`]="{ item }">
                      <span class="font-weight-bold">
                        {{ item.appointment.code ? item.appointment.code : '---' }}
                      </span>
                    </template>

                    <template v-slot:[`item.appointment.date`]="{ item }">
                      <span>{{ item.appointment.date ? formatDate(item.appointment.date) : '---' }}</span>
                    </template>

                    <template v-slot:[`item.appointment.type`]="{ item }">
                      <span>{{ item.appointment.type ? $t(`type.${item.appointment.type}`) : '---' }}</span>
                    </template>

                    <template v-slot:[`item.error`]="{ item }">
                      <v-icon :color="item.error ? 'error' : 'success'">
                        {{ item.error ? 'mdi-close' : 'mdi-check'}}
                      </v-icon>
                    </template>

                    <template v-slot:expanded-item="{ headers, item }">
                      <td class="ma-0 pa-0" v-if="item.error" :colspan="headers.length">
                        <v-card class="pa-5 grey lighten-4" elevation="0" tile flat>
                          <v-icon color="error" small class="mb-1 mr-2">
                            mdi-alert-circle-outline
                          </v-icon>
                          <span class="error--text">
                            {{ item.error.message }}
                          </span>
                        </v-card>
                      </td>
                    </template>

                    <template v-slot:[`item.data-table-expand`]="{ item, isExpanded, expand }">
                      <v-btn icon v-if="item.error" @click="expand(!isExpanded)">
                        <v-icon>
                          {{ isExpanded ? 'mdi-menu-up' : 'mdi-menu-down' }}
                        </v-icon>
                      </v-btn>
                    </template>
                  </v-data-table>
                </v-col>
              </v-row>

              <v-row>
                <v-col class="d-flex justify-end">
                  <v-btn
                    class="px-8"
                    @click="close"
                    :disabled="!batchImportEnded || (!downloadedErrorsFile && batchAppointmentsWithError.length > 0)"
                    elevation="0"
                    color="primary">
                    {{ $t('batchSchedule.buttons.ok') }}
                  </v-btn>
                </v-col>
              </v-row>
            </div>
          </div>

          <div v-else>
            <v-row>
              <v-col>
                <p>
                  {{ $t('batchSchedule.message.explanation', { value: explanationContext } ) }}
                </p>
                <a :href="batchScheduleExampleUrl" target="_blank" >
                  <span>
                    {{ $t('batchSchedule.label.csvExample') }}
                  </span>
                  <v-icon color="primary" small>
                    mdi-download
                  </v-icon>
                </a>
              </v-col>
            </v-row>

            <v-row v-if="isServiceProviderUser">
              <v-col>
                <v-select
                  @change="handleRealEstateSelect"
                  item-text="name"
                  item-value="id"
                  :items="serviceProviderCustomers"
                  :label="$t('batchSchedule.label.realEstate')"
                  return-object
                  outlined
                />
              </v-col>
            </v-row>

            <v-row v-if="isServiceProviderUser && selectedRealEstateId && hasMultipleBranches">
              <v-col>
                <v-select
                  @change="handleBranchSelect"
                  item-text="name"
                  item-value="id"
                  :items="branches"
                  :label="$t('batchSchedule.label.branch')"
                  return-object
                  outlined
                />
              </v-col>
            </v-row>

            <v-row v-if="!isServiceProviderUser || selectedRealEstateBranchId" no-gutters>
              <v-col>
                <HUpload
                  @change="handleGetSpreadsheetFile"
                  :loading="fileLoading"
                  multiple
                  small
                />
              </v-col>
            </v-row>
          </div>
      </v-card-text>
    </v-card>

  </v-dialog>
    <ModalConfirmBatchSchedulingClose />
    </div>
</template>

<script>
import ModalTitle from '@/components/app/modal/ModalTitle'
import { catalogDate, getAttributeNames, getKeyLocationByName, getKeyPickerByName, getMeetingLocationByName, getServiceTypeByName } from '@/util/catalog'
import moment from 'moment'
import { mapActions, mapGetters, mapMutations } from 'vuex'
import { appointmentsToScheduleHeaders, headers } from '../../../views/listHeaders/batchScheduleHeaders'
import InformationTooltip from '../../app/InformationTooltip.vue'
import HUpload from '../../lib/HUpload'
import AppointmentFilesUpload from '../AppointmentFilesUpload'
import ModalConfirmBatchSchedulingClose from './ModalConfirmBatchSchedulingClose'
import { isDraftAllowedBrazilianCustomer } from '../../../util/customer'
import { normalizeTextSearch } from '@/util/helpers'

export default {
  name: 'ModalBatchSchedule',

  components: {
    ModalTitle,
    AppointmentFilesUpload,
    HUpload,
    ModalConfirmBatchSchedulingClose,
    InformationTooltip,
  },

  data() {
    return {
      currentTab: 0,
      headers: headers,
      appointmentsToScheduleHeaders: appointmentsToScheduleHeaders,
      appointmentsToSchedule: [],
      currentScheduling: 0,
      scheduling: false,
      fileLoading: false,
      expanded: [],
      timeout: null,
      attachment: null,
      draftFile: false,
      downloadedErrorsFile: false,
      attachedCsvAsString: null,
      selectedRealEstateId: null,
      selectedRealEstateBranchId: null,
    }
  },

  mounted() {
    this.currentTab = this.isAustralian ? 1 : 0
    this.resetFields()
    if (this.isServiceProviderUser) {
      this.listServiceProviderCustomers()
    }
  },

  computed: {
    ...mapGetters(['isLoading', 'batchAppointments', 'batchAppointmentsWithError', 'activeModal', 'temporaryFile', 'currentRealEstate', 'serviceProviderCustomers', 'branches']),

    isServiceProviderUser() {
      const userRole = localStorage.getItem('access_roles')
      return !!userRole && userRole.includes('ROLE_SERVICE_PROVIDER')
    },

    hasMultipleBranches() {
      return this.branches && this.branches.length > 1
    },

    batchImportEnded() {
      return this.batchAppointments.length + this.batchAppointmentsWithError.length == this.appointmentsToSchedule.length
    },

    batchAppointmentsCombined() {
      return [...this.batchAppointmentsWithError, ...this.batchAppointments]
    },

    dialog() {
      return this.activeModal === 'BATCH_SCHEDULE_MODAL'
    },

    notOnUploadStep() {
      return this.appointmentsToSchedule && this.appointmentsToSchedule.length
    },

    explanationContext() {
      return (this.isImportingAppointments ? this.$t('batchSchedule.label.appointments') : this.$t('batchSchedule.label.drafts')).toLowerCase()
    },

    batchScheduleExampleUrl() {
      if (this.isAustralian) {
        return this.isImportingAppointments
          ? 'https://hauseful-static.s3.amazonaws.com/appointment-spreadsheet-template.csv'
          : 'https://hauseful-static.s3.amazonaws.com/draft-spreadsheet-template.csv'
      } else {
        return 'https://hauseful-static.s3.amazonaws.com/exemplo-batch.csv'
      }
    },

    isAustralian() {
      return localStorage.getItem('user_country') == 'AU'
    },

    isAllowedCustomer() {
      return isDraftAllowedBrazilianCustomer()
    },

    currentLanguage() {
      return localStorage.getItem('user_language')
    },

    isImportingAppointments() {
      return this.currentTab == 0
    },
  },

  methods: {
    ...mapMutations(['closeModal', 'openModal', 'setIsLoading', 'setBatchAppointments', 'setBatchAppointmentsWithError']),
    ...mapActions(['batchSchedule', 'batchPreschedule', 'temporaryFileUpload', 'listServiceProviderCustomers', 'fetchBranches']),
    formatDate: catalogDate,
    findServiceTypeByName: getServiceTypeByName,
    findMeetingLocationByName: getMeetingLocationByName,
    findKeyLocationByName: getKeyLocationByName,
    findKeyPickerByName: getKeyPickerByName,

    async schedule() {
      this.scheduling = true
      this.setIsLoading(true)

      if (this.attachment) {
        await this.temporaryFileUpload({ file: this.attachment })
      }
      this.appointmentsToSchedule.forEach((appointment, index) => {
        setTimeout(async () => {
          var appointmentRequest = this.prepareAppointment(appointment, index)
          if (this.temporaryFile && this.temporaryFile.id) {
            appointmentRequest['temporary_file_id'] = this.temporaryFile.id
            appointmentRequest['folder_name'] = 'files'
          }
          if (this.isAustralian) {
            await this.batchPreschedule(appointmentRequest)
          } else {
            if (!this.isImportingAppointments) appointmentRequest['appointment_request_type'] = 'DRAFT'
            await this.batchSchedule(appointmentRequest)
          }
          this.currentScheduling += 1
        }, index * 1000);
      })
    },

    downloadErrorsFile() {
      const lines = this.attachedCsvAsString.split("\n");
      const indexesToKeep = new Set(this.batchAppointmentsWithError.map(withError => withError.index + 1));
      const filteredLines = lines.filter((line, i) => i === 0 || indexesToKeep.has(i));
      const csvContent = filteredLines.join("\n");

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)
      link.setAttribute('href', url)
      const filename = this.isAustralian ? 'appointments_with_errors.csv' : 'agendamentos_com_erros.csv'
      link.setAttribute('download', filename)
      document.body.appendChild(link)
      link.click()

      document.body.removeChild(link)
      URL.revokeObjectURL(url)
      this.downloadedErrorsFile = true
    },

    prepareAppointment(appointment, index) {
      if(this.isAustralian){
        return this.buildDraft(appointment, index)
      } else {
        return this.buildAppointment(appointment, index)
      }
    },
    buildAppointment(appointment, index){
      let data = {
          index: index,
          type: this.findServiceTypeByName(appointment[getAttributeNames('type',this.currentLanguage)]),
          realty_code: appointment[getAttributeNames("realty_code", this.currentLanguage)],
          realty_description: appointment[getAttributeNames("realty_description", this.currentLanguage)],
          date: this.getParsedDate(appointment),
          address: this.getParsedAddress(appointment),
          additional_address_data: appointment[getAttributeNames("additional_address_data", this.currentLanguage)],
          square_meters:appointment[getAttributeNames("square_meters", this.currentLanguage)],
          private_area_meters: appointment[getAttributeNames("private_area_meters", this.currentLanguage)],
          observations: appointment[getAttributeNames("observations", this.currentLanguage)],
          external_code: appointment[getAttributeNames("external_code", this.currentLanguage)],
          displacement:appointment[ getAttributeNames("displacement", this.currentLanguage)],
          needs_authorization: appointment[getAttributeNames("needs_authorization", this.currentLanguage)],
          meeting_location: this.findMeetingLocationByName(appointment[getAttributeNames('meeting_location',this.currentLanguage)]),
          meeting_location_address: appointment[getAttributeNames("meeting_location_address", this.currentLanguage)],
          key_location: this.findKeyLocationByName(appointment[getAttributeNames('key_location',this.currentLanguage)]),
          key_location_address: appointment[getAttributeNames("key_location_address", this.currentLanguage)],
          key_picker: this.findKeyPickerByName(appointment[getAttributeNames('key_picker',this.currentLanguage)]),
          visitor_name: appointment[getAttributeNames("visitor_name", this.currentLanguage)],
          visitor_mail: appointment[getAttributeNames("visitor_mail", this.currentLanguage)],
          visitor_phone: appointment[getAttributeNames("visitor_phone", this.currentLanguage)],
          has_trousseau: appointment[getAttributeNames("has_trousseau", this.currentLanguage)],
          has_furniture: appointment[getAttributeNames("has_furniture", this.currentLanguage)],
          slot_size:appointment[getAttributeNames("time_slot", this.currentLanguage)]
      }
      if (this.isServiceProviderUser) {
        data.real_estate_id = this.selectedRealEstateId
        data.real_estate_branch_id = this.selectedRealEstateBranchId
      }
      return data
    },
    buildDraft(appointment, index){
      let data = {
          index: index,
          type: this.findServiceTypeByName(appointment[getAttributeNames('type',this.currentLanguage)]),
          realty_code: appointment[getAttributeNames("realty_code", this.currentLanguage)],
          date: this.getParsedDate(appointment),
          address: this.getParsedAddress(appointment),
          additional_address_data: appointment[getAttributeNames("additional_address_data", this.currentLanguage)],
          observations: appointment[getAttributeNames("observations", this.currentLanguage)],
          external_code: appointment[getAttributeNames("external_code", this.currentLanguage)],
          meeting_location: this.findMeetingLocationByName(appointment[getAttributeNames('meeting_location',this.currentLanguage)]),
          meeting_location_address: appointment[getAttributeNames("meeting_location_address", this.currentLanguage)],
          key_location: this.findKeyLocationByName(appointment[getAttributeNames('key_location',this.currentLanguage)]),
          key_location_address: appointment[getAttributeNames("key_location_address", this.currentLanguage)],
          key_picker: this.findKeyPickerByName(appointment[getAttributeNames('key_picker',this.currentLanguage)]),
          visitor_name: appointment[getAttributeNames("visitor_name", this.currentLanguage)],
          visitor_mail: appointment[getAttributeNames("visitor_mail", this.currentLanguage)],
          visitor_phone: appointment[getAttributeNames("visitor_phone", this.currentLanguage)],
          needs_authorization:false,
          update_additional_data:this.buildAdditionalData(appointment),
          slot_size:appointment[getAttributeNames("time_slot", this.currentLanguage)]
      }
      if (this.isServiceProviderUser) {
        data.real_estate_id = this.selectedRealEstateId
        data.real_estate_branch_id = this.selectedRealEstateBranchId
      }

      return data
    },
    buildAdditionalData(appointment) {
      var request = {
        additional_data_list_request:[]
      }

      for (const key in appointment) {
        let type
        if (key.startsWith('link:')) {
          type = 'LINK'
        } else if (key.startsWith('other:')) {
          type = 'OTHER'
        } else if (key.startsWith('phone:')) {
          type = 'PHONE'
        } else if (key.startsWith('email:')) {
          type = 'EMAIL'
        }

        if (type) {
          const firstColumnIndex = key.indexOf(':')
          const rawFieldName = key.substring(firstColumnIndex + 1)
          const fieldName = rawFieldName.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase()).trim()
          const fieldValue = appointment[key]
          request.additional_data_list_request.push({
            title: fieldName,
            value: fieldValue,
            type: type
          })
        }
      }

      return request
    },
    getParsedDate(appointment){
      var date =  appointment[getAttributeNames("date", this.currentLanguage)]
      var time =  appointment[getAttributeNames("time", this.currentLanguage)]
      return moment(`${date} ${time}`, 'DD/MM/YYYY HH:mm:ss')
    },
    getParsedAddress(appointment){
      var address = appointment[getAttributeNames("address", this.currentLanguage)]
      var neighborhood = appointment[getAttributeNames("neighborhood", this.currentLanguage)]
      var city = appointment[getAttributeNames("city", this.currentLanguage)]
      var number = appointment[getAttributeNames("number", this.currentLanguage)]
      var zipCode = appointment[getAttributeNames("zipCode", this.currentLanguage)]
      var state = appointment[getAttributeNames("state", this.currentLanguage)]
      var country = appointment[getAttributeNames("country", this.currentLanguage)]

      if(this.isAustralian)
        return `${address}, ${city} ${zipCode} ${state}, ${country}`
      else
        return `${address}, ${number} - ${neighborhood}, ${city}`
    },
    handleGetAttachment(file) {
      this.attachment = file[0]
    },

    handleRealEstateSelect(customer) {
      this.selectedRealEstateId = customer?.id ?? null
      this.selectedRealEstateBranchId = null
      if (this.selectedRealEstateId) {
        this.fetchBranches(this.selectedRealEstateId).then(() => {
          if (!this.hasMultipleBranches && this.branches[0]) {
            this.selectedRealEstateBranchId = this.branches[0].id
          }
        })
      }
    },

    handleBranchSelect(branch) {
      this.selectedRealEstateBranchId = branch?.id ?? null
    },
    handleGetSpreadsheetFile(file) {
      let reader = new FileReader()

      reader.readAsText(file[0])
      reader.onload = () => {
        this.fileLoading = true;
        let csvAsString = reader.result
        this.attachedCsvAsString = csvAsString
        let lines = csvAsString.split('\n')
        let headers = lines[0].split(',')
        lines.splice(0, 1)

        lines.forEach(line => {
          let obj = {}
          line = line.replace(/"[^"]+"/g, (v) => {
            return v.replace(/,/g, '###');
          });
          var regex = /"/g
          line = regex[Symbol.replace](line, '')
          line = line.split(',')

          for (let i = 0; i < headers.length; i++) {
            if (line[i] && headers[i]) {
              line[i] = line[i].replace(/###/g, ',')
              let key = this.formatJsonObjectKey(headers[i].trim())
              let value = this.formatJsonObjectValue(line[i].trim())
              obj[key] = value
            }
          }
          if (obj && Object.keys(obj).length > 1) {
            this.appointmentsToSchedule.push(obj)
          }
        })
        this.fileLoading = false
      }
    },

    formatJsonObjectKey(string) {
      return this.normalizeAndLowerCase(string.replace(/\./g, "").replace(/\s/g, '_'))
    },

    formatJsonObjectValue(string) {
      let normalizedString = this.normalizeAndLowerCase(string.replace(/\./g, "").replace(/\s/g, ''))
      if (normalizedString == 'sim' || normalizedString == 'yes') {
        return true
      } else if (normalizedString == 'nao' || normalizedString == 'no') {
        return false
      } else {
        return string
      }

    },

    normalizeAndLowerCase(string) {
      return normalizeTextSearch(string)
    },

    resetFields() {
      this.setBatchAppointments([])
      this.setBatchAppointmentsWithError([])
      this.appointmentsToSchedule = []
      this.fileLoading = false
      this.scheduling = false
      this.currentScheduling = 0
      this.attachment = null
      this.downloadedErrorsFile = false
      this.selectedRealEstateId = null
      this.selectedRealEstateBranchId = null
    },

    close() {
      this.resetFields()
      this.closeModal('BATCH_SCHEDULE_MODAL')
    },

    confirmClose() {
      this.notOnUploadStep ? this.openModal('CONFIRM_CLOSE_BATCH_SCHEDULE_MODAL') : this.close()
    }
  },
}
</script>
